import {
  Divider,
  Button,
  Space,
  Tooltip,
  Typography,
  Table,
  message,
  Form,
  Tag,
  Modal,
  Row,
  Col,
  Input,
  Select,
  Switch,
} from "antd"
import { Trans, useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useCallback, useEffect, useState } from "react"
import { RootState } from "app/store"
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  CloseCircleFilled,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons"
import { ColumnsType, TablePaginationConfig } from "antd/lib/table"
import {
  positionsSliceActions,
  positionsSliceSelectors,
} from "./PositionsSlice"
import confirm from "antd/lib/modal/confirm"
import { FeatureState } from "models/FeatureState"
import {
  formItemRequired,
  formItemRequiredNoWhitespace,
} from "utils/formItemRules"
import useSSTablePagination from "hooks/useSSTablePagination"
import { PositionDto, SiteDto } from "api/generated/namecheck"
import { getSiteTagColor, getTagColor } from "./utils"
import TextArea from "antd/lib/input/TextArea"
import { mapToPositionEdit, PositionEdit } from "models/PositionEdit"

const PositionsList: React.FC = () => {
  const { pagination, list, actionState, sites } = useSelector(
    (state: RootState) => state.positions
  )

  const [showCreateModal, setShowCreateModal] = useState<any>(false)
  const [editedPosition, setEditedPosition] = useState<PositionDto | undefined>(
    undefined
  )

  const loading = useSelector(positionsSliceSelectors.isLoading)

  const { t } = useTranslation()

  const [form] = Form.useForm()

  const onClickCreate = (event: any): void => {
    form.resetFields()
    setEditedPosition(undefined)

    setShowCreateModal(true)
  }

  const onClickEdit = (data: PositionDto): void => {
    setEditedPosition(data)
    form.setFieldsValue(mapToPositionEdit(data))

    setShowCreateModal(true)
  }

  const onFinish = async (values: PositionEdit): Promise<any> => {
    editedPosition
      ? await dispatch(positionsSliceActions.updatePosition(values))
      : await dispatch(positionsSliceActions.createPosition(values))

    loadTableData()

    editedPosition
      ? message.success(t("pages.Positions.edit.modal.responseSuccess"))
      : message.success(t("pages.Positions.create.modal.responseSuccess"))

    dispatch(positionsSliceActions.resetActionState())
    setShowCreateModal(false)
  }

  const paginationConfig = useSSTablePagination(pagination)

  const onClickActivate = (data: PositionDto): void => {
    confirm({
      type: "info",
      title: t("pages.Positions.activate.modal.title"),
      content: (
        <Trans
          i18nKey="pages.Positions.activate.modal.content"
          defaults={t("pages.Positions.activate.modal.content")}
          values={{ positionInfo: data?.name }}
          components={{ tag: <Tag className="modal-info-tag" /> }}
        />
      ),
      icon: <CheckCircleFilled />,
      okText: t("pages.Positions.activate.modal.ok"),
      cancelText: t("pages.Positions.modal.cancel"),
      onOk: async (e: any) => {
        await dispatch(positionsSliceActions.activatePosition(data.id ?? -1))
        message.success(t("pages.Positions.activate.response"), 5)
        loadTableData()
      },
    })
  }

  const onClickDeactivate = (data: PositionDto): void => {
    confirm({
      type: "error",
      title: t("pages.Positions.deactivate.modal.title"),
      content: (
        <Trans
          i18nKey="pages.Positions.deactivate.modal.content"
          defaults={t("pages.Positions.deactivate.modal.content")}
          values={{ positionInfo: data?.name }}
          components={{ tag: <Tag className="modal-info-tag" /> }}
        />
      ),
      icon: <CloseCircleFilled />,
      okText: t("pages.Positions.deactivate.modal.ok"),
      okButtonProps: { danger: true },
      cancelText: t("pages.Positions.modal.cancel"),
      cancelButtonProps: { danger: true },
      onOk: async (e: any) => {
        await dispatch(positionsSliceActions.deactivatePosition(data.id ?? -1))
        message.success(t("pages.Positions.deactivate.response"), 5)
        loadTableData()
      },
    })
  }

  const onClickDelete = (data: PositionDto): void => {
    confirm({
      type: "error",
      title: t("pages.Positions.delete.modal.title"),
      content: (
        <Trans
          i18nKey="pages.Positions.delete.modal.content"
          defaults={t("pages.Positions.delete.modal.content")}
          values={{ positionInfo: data?.name }}
          components={{ tag: <Tag className="modal-info-tag" /> }}
        />
      ),
      icon: <DeleteOutlined />,
      okText: t("pages.Positions.delete.modal.ok"),
      okButtonProps: { danger: true },
      cancelText: t("pages.Positions.modal.cancel"),
      cancelButtonProps: { danger: true },
      onOk: async (e: any) => {
        await dispatch(positionsSliceActions.deletePosition(data.id ?? -1))
        message.warning(t("pages.Positions.delete.response"), 5)
        loadTableData()
      },
    })
  }

  const columns: ColumnsType<PositionDto> = [
    {
      title: t("labels.Positions.name"),
      dataIndex: ["name"],
    },
    {
      title: t("labels.Positions.isActive"),
      dataIndex: ["isActive"],
      render: (isActive: boolean) => (
        <Tag color={getTagColor(isActive)}>
          {isActive
            ? t("labels.Positions.isActived")
            : t("labels.Positions.isDeactived")}
        </Tag>
      ),
    },
    {
      title: t("labels.Positions.positionCode"),
      dataIndex: ["positionCode"],
    },
    {
      title: t("labels.Positions.sites"),
      dataIndex: "sites",
      render: (sites: SiteDto[]) => (
        <>
          {sites.map((site: SiteDto) => (
            <Tag color={getSiteTagColor(site)} key={"site_" + site.code}>
              {site.name}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: t("labels.Positions.comment"),
      dataIndex: ["comment"],
    },
    {
      title: "",
      key: "actions",
      fixed: "right",
      align: "right",
      render: (text: string, record: PositionDto) => (
        <Space size="small" align="center">
          <Tooltip title={t("labels.Positions.edit")} key="view">
            <Button
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => onClickEdit(record)}
            />
          </Tooltip>
          {record.isActive ? (
            <Tooltip title={t("labels.Positions.deactivate")} key="deactivate">
              <Button
                shape="circle"
                icon={<CloseCircleOutlined />}
                ghost
                danger
                onClick={() => onClickDeactivate(record)}
              />
            </Tooltip>
          ) : (
            <Tooltip title={t("labels.Positions.activate")} key="activate">
              <Button
                shape="circle"
                icon={<CheckCircleOutlined />}
                type="primary"
                ghost
                onClick={() => onClickActivate(record)}
              />
            </Tooltip>
          )}
          <Tooltip title={t("labels.Positions.delete")} key="delete">
            <Button
              type="primary"
              shape="circle"
              icon={<DeleteOutlined />}
              danger
              onClick={() => onClickDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  const dispatch = useDispatch()

  const loadTableData = useCallback(
    () =>
      dispatch(
        positionsSliceActions.loadData({
          page: pagination.page,
          take: pagination.take,
        })
      ),
    [dispatch, pagination.page, pagination.take]
  )

  useEffect(() => {
    dispatch(positionsSliceActions.fetchSites())
    loadTableData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTableChange = useCallback(
    (newPaginationConfig: TablePaginationConfig) => {
      dispatch(
        positionsSliceActions.loadData({
          page: (newPaginationConfig.current ?? 1) - 1,
          take: newPaginationConfig.pageSize,
        })
      )
    },
    [dispatch]
  )

  return (
    <>
      <Typography.Title>
        <div>{t("pages.Positions.title")}</div>
        <Divider />
        <Button size="large" type="primary" onClick={onClickCreate}>
          <PlusOutlined /> {t("forms.Positions.createPosition")}
        </Button>
      </Typography.Title>

      <Table
        dataSource={list}
        columns={columns}
        loading={loading}
        rowKey="id"
        scroll={{ x: true }}
        pagination={paginationConfig}
        onChange={handleTableChange}
      />
      <Modal
        title={
          editedPosition
            ? t("pages.Positions.edit.modal.title")
            : t("pages.Positions.create.modal.title")
        }
        visible={showCreateModal}
        okText={
          editedPosition
            ? t("pages.Positions.edit.modal.ok")
            : t("pages.Positions.create.modal.ok")
        }
        onOk={() => form.submit()}
        cancelText={t("pages.Positions.modal.cancel")}
        onCancel={() => setShowCreateModal(false)}
        okButtonProps={{
          loading: actionState === FeatureState.Loading,
        }}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={24}>
            <Form.Item
              hidden
              name="id"
              rules={editedPosition ? formItemRequired : undefined}
            >
              <Input type="number" hidden />
            </Form.Item>
            <Col span={24} key="name">
              <Form.Item
                label={t("labels.Positions.name")}
                name="name"
                rules={formItemRequired}
              >
                <Input type="text" maxLength={255} />
              </Form.Item>
            </Col>
            <Col span={24} key="positionCode">
              <Form.Item
                label={t("labels.Positions.positionCode")}
                name="positionCode"
                rules={formItemRequiredNoWhitespace}
              >
                <Input type="text" maxLength={255} />
              </Form.Item>
            </Col>
            <Col span={24} key="isActive">
              <Form.Item label={t("labels.Positions.isActive")} name="isActive">
                <Switch
                  size="default"
                  checkedChildren={t("labels.Positions.isActived")}
                  unCheckedChildren={t("labels.Positions.isDeactived")}
                  defaultChecked={editedPosition?.isActive ?? true}
                />
              </Form.Item>
            </Col>
            <Col span={24} key="comment">
              <Form.Item label={t("labels.Positions.comment")} name="comment">
                <TextArea maxLength={255} />
              </Form.Item>
            </Col>
            <Col span={24} key="sites">
              <Form.Item
                label={t("labels.Positions.sites")}
                name="sites"
                required
              >
                <Select<string>
                  mode="multiple"
                  placeholder={t("labels.Positions.sitesPlaceholder")}
                  style={{ width: "100%" }}
                >
                  {sites.map((item: SiteDto) => (
                    <Select.Option
                      key={"site_opt_" + (item.code ?? "")}
                      value={item.code ?? ""}
                    >
                      {item?.name ?? ""}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  )
}

export default PositionsList
